// Ghost's comments UMD has no public unmount API. Give each article's comments
// their own same-origin window so leaving the article destroys its entire runtime.
function comments(root, signal) {
    root.querySelectorAll('template[data-ghost-comments]').forEach(template => {
        const source = template.content.querySelector('script[data-ghost-comments]');
        if (!source) return;
        const frame = document.createElement('iframe');
        frame.className = 'ghost-comments-frame';
        frame.title = 'Comments';
        frame.style.cssText = 'display:block;width:100%;height:180px;border:0';
        template.after(frame);
        signal.addEventListener('abort', () => frame.remove(), {once: true});
        frame.addEventListener('load', () => {
            if (signal.aborted) return;
            const doc = frame.contentDocument, win = frame.contentWindow;
            win.history.replaceState(null, '', location.href);
            const resize = () => { if (frame.isConnected) frame.style.height = `${Math.max(180, doc.body.scrollHeight)}px`; };
            const observer = new ResizeObserver(() => { resize(); syncTheme(); });
            observer.observe(doc.body);
            const syncTheme = () => {
                doc.body.style.color = getComputedStyle(document.body).color;
                doc.body.style.fontFamily = getComputedStyle(document.body).fontFamily;
                const dark = document.documentElement.dataset.theme === 'dark';
                doc.documentElement.style.colorScheme = dark ? 'dark' : 'light';
                doc.documentElement.classList.toggle('dark', dark);
                doc.querySelector('iframe[title="comments-frame"]')?.contentDocument?.documentElement?.classList.toggle('dark', dark);
            };
            const theme = new MutationObserver(syncTheme);
            theme.observe(document.documentElement, {attributes: true, attributeFilter: ['data-theme']});
            syncTheme();
            // Comments use location.hash to open Ghost Portal and link to comments.
            // Portal itself lives once in the persistent parent shell.
            const replace = win.history.replaceState.bind(win.history);
            let syncingHistory = false;
            const syncHash = () => {
                if (!frame.isConnected || win.location.hash === location.hash) return;
                const oldURL = win.location.href;
                syncingHistory = true;
                replace(null, '', location.href);
                syncingHistory = false;
                win.dispatchEvent(new HashChangeEvent('hashchange', {oldURL, newURL: location.href}));
            };
            const navigateHash = (url, replaceEntry = false) => {
                if (url.hash === location.hash) return;
                if (replaceEntry) {
                    const oldURL = location.href;
                    history.replaceState(history.state, '', url.href);
                    window.dispatchEvent(new HashChangeEvent('hashchange', {oldURL, newURL: url.href}));
                } else location.hash = url.hash;
                syncHash();
            };
            // Keep a single parent history entry for Portal/comment hashes. Letting
            // the child add one first would require two Back presses to leave it.
            win.navigation?.addEventListener('navigate', event => {
                if (syncingHistory) return;
                const url = new URL(event.destination.url);
                if (event.cancelable && event.destination.sameDocument && ['push', 'replace'].includes(event.navigationType)) {
                    event.preventDefault();
                    navigateHash(url, event.navigationType === 'replace');
                }
            }, {signal});
            // Ghost's "back to parent comment" uses pushState directly; it must
            // update the outer permalink without adding a child history entry.
            win.history.pushState = (_state, _title, url) => navigateHash(new URL(url, location.href));
            win.addEventListener('hashchange', () => {
                if (win.location.hash !== location.hash) navigateHash(new URL(win.location.href));
            }, {signal});
            window.addEventListener('hashchange', syncHash, {signal});
            const script = doc.createElement('script');
            [...source.attributes].forEach(({name, value}) => script.setAttribute(name, value));
            let scriptTimer;
            const unavailable = () => {
                clearTimeout(scriptTimer);
                script.remove();
                doc.body.textContent = 'Comments are unavailable. Reload this page to try again.';
                resize();
            };
            script.addEventListener('load', () => { clearTimeout(scriptTimer); resize(); }, {once: true});
            script.addEventListener('error', unavailable, {once: true});
            scriptTimer = setTimeout(unavailable, 8000);
            doc.body.append(script);
            signal.addEventListener('abort', () => { clearTimeout(scriptTimer); observer.disconnect(); theme.disconnect(); frame.remove(); }, {once: true});
        }, {once: true});
        frame.src = template.dataset.frameSrc;
    });
}

function execute(source, parent, signal) {
    if (signal.aborted) return Promise.resolve();
    const script = document.createElement('script');
    [...source.attributes].forEach(({name, value}) => script.setAttribute(name, value));
    script.removeAttribute('defer');
    script.async = false;
    script.textContent = source.textContent;
    return new Promise(resolve => {
        let timer;
        const finish = () => { clearTimeout(timer); resolve(); };
        if (script.src || script.type === 'module') {
            script.addEventListener('load', finish, {once: true});
            script.addEventListener('error', finish, {once: true});
            timer = setTimeout(finish, 6000);
        }
        signal.addEventListener('abort', () => { script.remove(); finish(); }, {once: true});
        parent.append(script);
        if (!script.src && script.type !== 'module') finish();
    });
}

export const executable = script => !script.type || /^(module|text\/javascript|application\/javascript)$/.test(script.type);

export default function articleRuntime(root, signal, {initial = false, extraScripts = []} = {}) {
    comments(root, signal);
    const announce = () => {
        if (!signal.aborted) root.dispatchEvent(new CustomEvent('tiwb:page-ready', {bubbles: true}));
    };
    if (initial) {
        // The initial deferred entry runs before DOMContentLoaded. Wait for the
        // document's other deferred scripts to install their lifecycle listeners.
        if (document.readyState === 'complete') queueMicrotask(announce);
        else document.addEventListener('DOMContentLoaded', announce, {once: true, signal});
        return;
    }
    return (async () => {
        // Scripts parsed by DOMParser are inert; execute article-owned scripts in
        // order after insertion. The theme, Portal and search are never replayed.
        for (const source of [...root.querySelectorAll('script')].filter(executable)) {
            const parent = source.parentElement;
            source.remove();
            await execute(source, parent, signal);
        }
        const cards = document.querySelector('script[src*="/public/cards.min.js"]');
        if (cards && root.querySelector('.kg-audio-card, .kg-video-card, .kg-toggle-card, .kg-gallery-card')) await execute(cards, root, signal);
        for (const source of extraScripts) await execute(source, root, signal);
        announce();
    })();
}
